import { useState } from 'react';
import { Modal, Backdrop, Select, FormControl, MenuItem, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { formatRoleName, getRoleFromPrivilege } from '../../../utils/utils';
import { ROLE_PRIVILEGES } from '../../../common/consts';
import { auth } from '../../../firebase';
import './styles.css';

const getInitials = (name, email) => {
	if (name && name.trim()) {
		return name
			.trim()
			.split(/\s+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((w) => w[0].toUpperCase())
			.join('');
	}
	return email ? email[0].toUpperCase() : '?';
};

function UserAdminModalBase({ type, users, open, toggleModal, updateOrDeleteUser, addNewUser, loading, title, description }) {
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');

	const validateEmail = (val) => /^\S+@\S+\.\S+$/.test(String(val).toLowerCase());

	const handleEmailChange = (e) => {
		const val = e.target.value;
		setEmail(val);
		if (emailError && validateEmail(val)) setEmailError('');
	};

	const handleAdd = async () => {
		if (!validateEmail(email)) {
			setEmailError('Please enter a valid email address.');
			return;
		}
		await addNewUser(email);
		setEmail('');
		setEmailError('');
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') handleAdd();
	};

	const isAdmins = type === 'admins';
	const modalTitle = title || (isAdmins ? 'Admins' : 'Users with Access');
	const modalDescription = description || (isAdmins 
		? 'Manage admin roles and privileges.'
		: 'Manage who has access to this organization.');

	return (
		<Modal
			open={open}
			onClose={toggleModal}
			slots={{ backdrop: Backdrop }}
			slotProps={{
				backdrop: { sx: { backgroundColor: 'rgba(15, 45, 78, 0.42) !important' } },
			}}
		>
			<div className="adm-dialog">
				{loading && (
					<div className="adm-loading-overlay">
						<CircularProgress size={30} sx={{ color: '#0f2d4e' }} />
					</div>
				)}

				{/* ── Header ── */}
				<div className="adm-header">
					<div className="adm-header__left">
						<h2 className="adm-title">{modalTitle}</h2>
						<p className="adm-subtitle">{modalDescription}</p>
					</div>
					<button className="adm-close-btn" onClick={toggleModal} aria-label="Close">
						<CloseIcon sx={{ fontSize: 17 }} />
					</button>
				</div>

				{/* ── Invite section ── */}
				<div className="adm-invite">
					<span className="adm-section-label">{isAdmins ? 'Invite admin' : 'Invite user'}</span>
					<div className={`adm-invite-row${emailError ? ' adm-invite-row--error' : ''}`}>
						<div className="adm-invite-input-wrap">
							<EmailOutlinedIcon className="adm-invite-icon" />
							<input
								className="adm-invite-input"
								type="email"
								placeholder="Enter email address"
								value={email}
								onChange={handleEmailChange}
								onKeyDown={handleKeyDown}
							/>
						</div>
						<button className="adm-invite-btn" onClick={handleAdd} disabled={loading}>
							{isAdmins ? 'Add admin' : 'Add user'}
						</button>
					</div>
					{emailError && <p className="adm-invite-error">{emailError}</p>}
				</div>

				{/* ── Current admins section ── */}
				<div className="adm-members">
					<div className="adm-members__header">
						<span className="adm-section-label">{isAdmins ? 'Current admins' : 'Current users'}</span>
						{users.length > 0 && (
							<span className="adm-members__count">
								{users.length} {users.length === 1 ? 'member' : 'members'}
							</span>
						)}
					</div>

					<div className="adm-list-panel">
						{users.length === 0 ? (
							<div className="adm-empty">
								<PersonOutlineIcon className="adm-empty__icon" />
								<p className="adm-empty__title">{isAdmins ? 'No admins yet' : 'No users yet'}</p>
								<p className="adm-empty__text">
									{isAdmins
										? 'Invite an admin using the field above.'
										: 'Invite a user using the field above.'}
								</p>
							</div>
						) : (
							<ul className="adm-list">
								{users.map((user, i) => {
									const isSelf = user.email === auth.currentUser?.email;
									const role = getRoleFromPrivilege(user.privilegeLevel);
									return (
										<li key={user.uid || i} className="adm-row">
											<div className="adm-row__avatar">
												{user.photoURL ? (
													<img src={user.photoURL} alt="" className="adm-avatar-img" />
												) : (
													<span className="adm-avatar-initials">
														{getInitials(user.displayName, user.email)}
													</span>
												)}
											</div>
											<div className="adm-row__identity">
												{user.displayName ? (
													<>
														<span className="adm-row__name">{user.displayName}</span>
														<span className="adm-row__email">{user.email}</span>
													</>
												) : (
													<span className="adm-row__name adm-row__name--email">
														{user.email}
													</span>
												)}
											</div>
											<div className="adm-row__role">
												{isAdmins ? (
													<FormControl size="small">
														<Select
															value={role}
															onChange={(e) => updateOrDeleteUser(user, e.target.value)}
															disabled={isSelf}
															sx={{
																width: 128,
																height: 34,
																fontFamily: 'Open Sans, sans-serif',
																fontSize: '13px',
																fontWeight: 500,
																borderRadius: '7px',
																color: '#0f2d4e',
																'& .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(15,45,78,0.17)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: isSelf
																		? 'rgba(15,45,78,0.17)'
																		: 'rgba(15,45,78,0.38)',
																},
																'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
																	borderColor: '#0f2d4e',
																	borderWidth: '1.5px',
																},
																'&.Mui-disabled': {
																	opacity: 0.65,
																	'& .MuiOutlinedInput-notchedOutline': {
																		borderColor: 'rgba(15,45,78,0.12)',
																	},
																},
																'& .MuiSelect-icon': {
																	color: 'rgba(15,45,78,0.4)',
																},
																'& .MuiSelect-select': {
																	paddingTop: '7px',
																	paddingBottom: '7px',
																},
															}}
															MenuProps={{
																PaperProps: {
																	sx: {
																		borderRadius: '8px',
																		boxShadow: '0 4px 16px rgba(15,45,78,0.14)',
																		mt: 0.5,
																	},
																},
															}}
														>
															{Object.keys(ROLE_PRIVILEGES).map((roleKey) => (
																<MenuItem
																	key={roleKey}
																	value={roleKey}
																	sx={{
																		fontFamily: 'Open Sans, sans-serif',
																		fontSize: '13px',
																		fontWeight: 500,
																		color: '#0f2d4e',
																	}}
																>
																	{formatRoleName(roleKey)}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												) : (
													<button
														className="adm-remove-btn"
														onClick={() => updateOrDeleteUser(user)}
													>
														Remove
													</button>
												)}
											</div>
										</li>
									);
								})}
							</ul>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
}

export default UserAdminModalBase;
